import { halifaxParts } from "./clock";
import type { FlightType } from "./types";

/** America/Halifax month 1–12. */
export function halifaxMonth(date: Date = new Date()): number {
  return Number(halifaxParts(date).month);
}

export function halifaxYear(date: Date = new Date()): number {
  return Number(halifaxParts(date).year);
}

export type SeasonId = "golf" | "shoulder-spring" | "shoulder-fall" | "winter";

export function seasonId(month: number): SeasonId {
  if (month >= 5 && month <= 10) return "golf";
  if (month === 4) return "shoulder-spring";
  if (month === 11) return "shoulder-fall";
  return "winter";
}

/**
 * Old Celtic fiction pads. Not a live home.
 * Inbox gravity uses the desk's current bases instead of this pair.
 */
export function seasonalPad(month: number): "CYPD" | "CYHZ" {
  return month >= 5 && month <= 10 ? "CYPD" : "CYHZ";
}

const ATLANTIC_BASES = new Set(["CYPD", "CYHZ", "CYQY", "CYYG"]);

export function isReposMonth(month: number): boolean {
  return month === 4 || month === 11;
}

export type TypeWeights = Record<FlightType, number>;

/** Seasonal purpose mix. Golf is a summer weight, not a Port Hawkesbury lock. */
export function typeWeights(month: number): TypeWeights {
  switch (month) {
    case 1:
      return { golf: 0, sun: 34, corporate: 18, vip: 10, medical: 8, ferry: 14 };
    case 2:
      return { golf: 0, sun: 32, corporate: 18, vip: 12, medical: 8, ferry: 14 };
    case 3:
      return { golf: 0, sun: 22, corporate: 24, vip: 16, medical: 8, ferry: 12 };
    case 4:
      return { golf: 0, sun: 8, corporate: 28, vip: 20, medical: 8, ferry: 18 };
    case 5:
      return { golf: 34, sun: 2, corporate: 18, vip: 16, medical: 8, ferry: 10 };
    case 6:
      return { golf: 38, sun: 0, corporate: 16, vip: 14, medical: 8, ferry: 6 };
    case 7:
      return { golf: 40, sun: 0, corporate: 14, vip: 14, medical: 8, ferry: 6 };
    case 8:
      return { golf: 38, sun: 0, corporate: 14, vip: 14, medical: 8, ferry: 6 };
    case 9:
      return { golf: 34, sun: 2, corporate: 18, vip: 16, medical: 8, ferry: 8 };
    case 10:
      return { golf: 22, sun: 8, corporate: 20, vip: 16, medical: 8, ferry: 12 };
    case 11:
      return { golf: 0, sun: 24, corporate: 20, vip: 14, medical: 8, ferry: 18 };
    default:
      return { golf: 0, sun: 34, corporate: 16, vip: 10, medical: 8, ferry: 14 };
  }
}

export function pickWeightedType(
  month: number,
  available: FlightType[],
  rng: () => number = Math.random,
): FlightType {
  const w = typeWeights(month);
  const rows = available.map((item) => ({ item, weight: Math.max(0, w[item]) }));
  const total = rows.reduce((s, r) => s + r.weight, 0);
  if (total <= 0) return available[0] ?? "corporate";
  let roll = rng() * total;
  for (const row of rows) {
    roll -= row.weight;
    if (roll <= 0) return row.item;
  }
  return rows[rows.length - 1]?.item ?? "corporate";
}

function liveBases(bases: readonly string[] = []): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of bases) {
    const icao = raw.trim().toUpperCase();
    if (icao.length < 3 || seen.has(icao)) continue;
    seen.add(icao);
    out.push(icao);
  }
  return out;
}

/** Season line. Names a pad only when that ICAO is a current company base. */
export function seasonCaption(month: number, year: number, bases: readonly string[] = []): string {
  const id = seasonId(month);
  const mon = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"][
    month - 1
  ];
  const season = id === "golf" ? "Golf season" : id === "winter" ? "Winter" : "Shoulder";
  const live = liveBases(bases);
  if (!live.length) return `${mon} ${year} · ${season}`;
  const atlantic = live.filter((icao) => ATLANTIC_BASES.has(icao));
  if (atlantic.length) {
    const pads = atlantic.filter((icao) => icao === "CYPD" || icao === "CYHZ");
    const named = (pads.length ? pads : atlantic).join(" / ");
    return `${mon} ${year} · ${season} · ${named}`;
  }
  return `${mon} ${year} · ${season} · ${live.join(", ")}`;
}

/** Client cities that feed Cabot Links / Cabot Cliffs via CYPD. Exec first; hubs last. */
export const GOLF_MARKETS = [
  "KTEB",
  "KHPN",
  "KBED",
  "CYHU",
  "CYHM",
  "KORH",
  "KPVD",
  "CYOW",
  "KDCA",
  "KHEF",
  "KMHT",
  "KACK",
  "KBGR",
  "KPBI",
  "KAPF",
  "KBCT",
  "CYYT",
  "CYHZ",
  "CYQB",
  "KBOS",
  "CYYZ",
  "CYUL",
  "KJFK",
  "KEWR",
  "KMIA",
  "KCLT",
] as const;

/** Southbound winter-escape fields that fit a Hawker from Cape Breton. */
export const SUN_DESTS = [
  "KAPF",
  "KBCT",
  "KOPF",
  "KFXE",
  "KPBI",
  "KTPA",
  "KRSW",
  "KJAX",
  "KCHS",
  "KMYR",
  "KILM",
  "MYNN",
  "TXKF",
  "TBPB",
  "TNCM",
  "MDPC",
  "MKJS",
  "MWCR",
  "TNCA",
  "TIST",
  "KMIA",
  "KFLL",
  "KMCO",
  "KCLT",
  "TJSJ",
] as const;

export const CORPORATE_DESTS = [
  "KTEB",
  "KHPN",
  "KBED",
  "CYHU",
  "CYHM",
  "CYKZ",
  "CYTZ",
  "CYKF",
  "KMMU",
  "KFRG",
  "KORH",
  "KSWF",
  "KHEF",
  "CYOW",
  "KDCA",
  "CYQB",
  "CYYT",
  "CYQM",
  "CYYG",
  "CYYZ",
  "CYUL",
  "KBOS",
  "KJFK",
  "KEWR",
  "KIAD",
  "KPHL",
  "KBWI",
] as const;

export const VIP_DESTS = [
  "KTEB",
  "KHPN",
  "KBED",
  "KACK",
  "KAPF",
  "KOPF",
  "CYHU",
  "CYTZ",
  "KDCA",
  "MYNN",
  "TXKF",
  "KBOS",
  "KMIA",
  "CYUL",
  "CYYZ",
] as const;

export const MEDICAL_DESTS = [
  "CYHZ",
  "CYYT",
  "CYQY",
  "CYHU",
  "KBED",
  "KDCA",
  "CYYZ",
  "KBOS",
  "CYUL",
  "KIAD",
  "KPHL",
] as const;

export const MAINT_DESTS = ["KICT", "KCPT", "KDAY", "KGJT", "KAPA", "KAPF", "KSDL", "KCHA"] as const;

export const PREFERRED_ICAO: Record<FlightType, readonly string[]> = {
  golf: GOLF_MARKETS,
  sun: SUN_DESTS,
  corporate: CORPORATE_DESTS,
  vip: VIP_DESTS,
  medical: MEDICAL_DESTS,
  ferry: MAINT_DESTS,
};

export function prefersType(type: FlightType, icao: string): boolean {
  return PREFERRED_ICAO[type].includes(icao);
}
